import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { container } from "tsyringe";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import UserAccountInfoResponseResource from "common/resources/User/UserAccountInfoResponseResource";
import Button, { EButtonColor, EButtonSize, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import CreateProjectModal from "components/materials/CreateProjectModal";
import HasRules from "components/materials/HasRules";
import HasSubscriptionPlan from "components/materials/HasSubscriptionPlan";
import I18n from "components/materials/I18n";
import UserBadge from "components/materials/UserBadge";
import ModuleConfig from "configs/ModuleConfig";
import AuthService from "services/AuthService";
import classNames from "classnames";
import classes from "./classes.module.scss";

const modules = container.resolve(ModuleConfig).get().modules;
const authService = container.resolve(AuthService);

type IProps = {
	user: UserAccountInfoResponseResource;
	className?: string;
};

export default function ConnectedHeader(props: IProps) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	const toggleCreateProjectModal = useCallback(() => setIsCreateProjectModalOpen((prev) => !prev), []);

	const toggleMenu = useCallback(() => {
		setIsMenuOpen((prev) => !prev);
	}, []);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (!isMenuOpen) return;
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsMenuOpen(false);
			}
		},
		[isMenuOpen],
	);

	const logout = useCallback(() => {
		authService.logout();
	}, []);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [handleClickOutside]);

	return (
		<div className={classNames(classes["root"], props.className)}>
			<HasSubscriptionPlan onNoRightComponent={<DepositProjectButton />}>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.MEDIUM} onClick={toggleCreateProjectModal}>
					<Typography type="span" size="medium" weight="medium">
						{I18n.trslt(I18n.asset.component.header.create_project)}
					</Typography>
				</Button>
			</HasSubscriptionPlan>

			<div className={classes["menu-container"]} onClick={toggleMenu}>
				<UserBadge user={props.user} />

				<ChevronDownIcon className={classes["icon"]} />

				{isMenuOpen && (
					<div className={classes["menu"]} ref={menuRef}>
						<Link to={modules.pages.Profile.props.path} className={classes["link"]}>
							<Typography type="span" size="medium" weight="medium" className={classes["whitespace-wrap"]}>
								{I18n.trslt(I18n.asset.component.header.personnal_informations)}
							</Typography>
						</Link>

						<Link to={modules.pages.MyProjects.props.path} className={classes["link"]}>
							<Typography type="span" size="medium" weight="medium" className={classes["whitespace-wrap"]}>
								{I18n.trslt(I18n.asset.component.header.my_projects)}
							</Typography>
						</Link>

						<HasRules requiredRules={{ AND: { access__admin_dashboard: true } }}>
							<Link to={modules.pages.AdminDashboard.props.path} className={classes["link"]}>
								<Typography type="span" size="medium" weight="medium" className={classes["whitespace-wrap"]}>
									{I18n.trslt(I18n.asset.component.header.all_projects)}
								</Typography>
							</Link>
						</HasRules>

						<div onClick={logout}>
							<Typography type="span" size="medium" weight="medium" className={classes["whitespace-wrap"]}>
								{I18n.trslt(I18n.asset.component.header.logout)}
							</Typography>
						</div>
					</div>
				)}
			</div>

			<CreateProjectModal isOpen={isCreateProjectModalOpen} onClose={toggleCreateProjectModal} />
		</div>
	);
}

function DepositProjectButton() {
	return (
		<Link to={modules.pages.SubmitProject.props.path} className={classes["link"]}>
			<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.MEDIUM}>
				<Typography type="span" size="medium" weight="medium">
					{I18n.trslt(I18n.asset.component.header.submit_project)}
				</Typography>
			</Button>
		</Link>
	);
}
